import { useTranslation } from 'react-i18next';
import { Heading, Flex, Spinner } from '@radix-ui/themes';

import SectionCard from '../../../../shared/SectionCard/SectionCard.jsx';
import { useReportsAiSummary } from '../data.js';
import { useCampaignFilter } from '../../hooks/useCampaignFilter.js';
import styles from './AiSummary.module.css';

const MiniBar = ({ value, max = 100, color = '#5C83FA' }) => (
  <div className={styles.miniBar}>
    <div
      className={styles.miniBarFill}
      style={{ width: `${(value / max) * 100}%`, background: color }}
    />
  </div>
);

const MetricCard = ({ value, label, suffix = '', icon, accent = false }) => (
  <div
    className={`${styles.metricCard} ${accent ? styles.metricCardAccent : ''}`}
  >
    {icon && (
      <div className={styles.metricIcon}>
        <i className={icon} />
      </div>
    )}
    <div className={styles.metricContent}>
      <span className={styles.metricValue}>
        {value}
        {suffix && <span className={styles.metricSuffix}>{suffix}</span>}
      </span>
      <span className={styles.metricLabel}>{label}</span>
    </div>
  </div>
);

export const AiSummary = ({ startDate, endDate }) => {
  const { t } = useTranslation('reports');
  const { campaignId } = useCampaignFilter();
  const { data, isLoading } = useReportsAiSummary({
    campaignId,
    startDate,
    endDate,
  });

  if (isLoading) {
    return (
      <SectionCard
        headerContent={
          <Flex align="center" gap="2">
            <i
              className="fa-regular fa-sparkles"
              style={{ color: 'var(--color-purple)' }}
            />
            <Heading size="4" m="0">
              {t('summary.ai.title')}
            </Heading>
          </Flex>
        }
      >
        <Flex align="center" justify="center" py="6">
          <Spinner size="3" />
        </Flex>
      </SectionCard>
    );
  }

  const totalConvos =
    data.conversationsByType.sms + data.conversationsByType.chat;
  const chatPercent =
    totalConvos > 0
      ? Math.round((data.conversationsByType.chat / totalConvos) * 100)
      : 0;
  const smsPercent = 100 - chatPercent;

  const totalFeedback =
    data.feedbackBreakdown.positive +
    data.feedbackBreakdown.negative +
    data.feedbackBreakdown.unclassified;
  const posPercent =
    totalFeedback > 0
      ? Math.round((data.feedbackBreakdown.positive / totalFeedback) * 100)
      : 0;
  const negPercent =
    totalFeedback > 0
      ? Math.round((data.feedbackBreakdown.negative / totalFeedback) * 100)
      : 0;

  return (
    <SectionCard
      headerContent={
        <Flex align="center" gap="2">
          <i
            className="fa-regular fa-sparkles"
            style={{ color: 'var(--color-purple)' }}
          />
          <Heading size="4" m="0">
            {t('summary.ai.title')}
          </Heading>
        </Flex>
      }
    >
      {/* Top: Key metrics row */}
      <div className={styles.metricsRow}>
        <MetricCard
          value={data.totalConversations.toLocaleString()}
          label={t('summary.ai.totalConversations')}
          icon="fa-regular fa-comments"
          accent
        />
        <MetricCard
          value={data.totalMessages.toLocaleString()}
          label={t('summary.ai.totalMessages')}
          icon="fa-regular fa-message-lines"
        />
        <MetricCard
          value={(data.uniqueUsers ?? 0).toLocaleString()}
          label={t('summary.ai.uniqueUsers')}
          icon="fa-regular fa-users"
        />
        <MetricCard
          value={data.avgMessagesPerConversation}
          label={t('summary.ai.avgPerConversation')}
          icon="fa-regular fa-chart-simple"
        />
      </div>

      {/* Bottom: Channel split + Feedback */}
      <div className={styles.bottomRow}>
        {/* Channel breakdown */}
        <div className={styles.breakdownSection}>
          <p className={styles.sectionLabel}>
            {t('summary.ai.channelBreakdown')}
          </p>
          <div className={styles.breakdownBars}>
            <div className={styles.breakdownItem}>
              <div className={styles.breakdownInfo}>
                <i className="fa-regular fa-message" />
                <span className={styles.breakdownName}>
                  {t('summary.ai.chatLabel')}
                </span>
                <span className={styles.breakdownCount}>
                  {data.conversationsByType.chat.toLocaleString()}
                </span>
                <span className={styles.breakdownPercent}>{chatPercent}%</span>
              </div>
              <MiniBar value={chatPercent} color="#5C83FA" />
            </div>
            <div className={styles.breakdownItem}>
              <div className={styles.breakdownInfo}>
                <i className="fa-regular fa-message-sms" />
                <span className={styles.breakdownName}>
                  {t('summary.ai.smsLabel')}
                </span>
                <span className={styles.breakdownCount}>
                  {data.conversationsByType.sms.toLocaleString()}
                </span>
                <span className={styles.breakdownPercent}>{smsPercent}%</span>
              </div>
              <MiniBar value={smsPercent} color="#5C83FA" />
            </div>
          </div>
        </div>

        {/* Feedback breakdown */}
        <div className={styles.breakdownSection}>
          <p className={styles.sectionLabel}>
            {t('summary.ai.feedbackBreakdown')}
          </p>
          <div className={styles.breakdownBars}>
            <div className={styles.breakdownItem}>
              <div className={styles.breakdownInfo}>
                <i className="fa-regular fa-thumbs-up" />
                <span className={styles.breakdownName}>
                  {t('summary.ai.positiveLabel')}
                </span>
                <span className={styles.breakdownCount}>
                  {data.feedbackBreakdown.positive.toLocaleString()}
                </span>
                <span className={styles.breakdownPercent}>{posPercent}%</span>
              </div>
              <MiniBar value={posPercent} color="#5C83FA" />
            </div>
            <div className={styles.breakdownItem}>
              <div className={styles.breakdownInfo}>
                <i className="fa-regular fa-thumbs-down" />
                <span className={styles.breakdownName}>
                  {t('summary.ai.negativeLabel')}
                </span>
                <span className={styles.breakdownCount}>
                  {data.feedbackBreakdown.negative.toLocaleString()}
                </span>
                <span className={styles.breakdownPercent}>{negPercent}%</span>
              </div>
              <MiniBar value={negPercent} color="#5C83FA" />
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
